import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';

import PauseIcon from 'components/common/icons/stroke/pause';
import PlayIcon from 'components/common/icons/stroke/play-stroke';
import Spinner from 'components/common/spinner_two';

/**
 * @visibleName Audio Player
 */

// output is 'mm:ss' (seconds < 3600), e.g. 63 => '01:03'
// or 'hh:mm:ss' (seconds >= 3600), e.g. 3601 => '01:00:01'
const formatSeconds = seconds => {
  const momentizedTime = moment.utc(seconds * 1000);
  const format = momentizedTime.hour() ? 'HH:mm:ss' : 'mm:ss';

  return momentizedTime.format(format);
};

export const useAudio = ({ enablePlayOnError, isPlaying, loadImmediately, onError, trackLabel, url }) => {
  const [hasError, setError] = useState(!url);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [initialized, setInitialized] = useState(loadImmediately);

  const toggle = () => {
    if (!initialized && !hasError) {
      setAudio(new Audio(url));
    }

    if (!hasError) {
      setPlaying(!playing);
      return;
    }

    if (hasError && enablePlayOnError && onError) {
      onError({
        detail:
          "Recording could not be played. Confirm that the recording is a supported format and isn't a corrupted file.",
        trackLabel: trackLabel || url,
      });
    }
  };

  const [audio, setAudio] = useState(loadImmediately ? new Audio(url) : null); // useState() prevents creation of multiple instances of the Audio

  useEffect(() => {
    if (initialized) {
      playing ? audio.play() : audio.pause();
    }
  }, [audio, initialized, playing]);

  useEffect(() => {
    setError(false);
    onError && onError(null);

    if (initialized) {
      audio.currentTime = 0;
      setPlaying(false);
    }
    // eslint-disable-next-line
  }, [url]);

  useEffect(() => {
    setPlaying(isPlaying);
  }, [isPlaying]);

  useEffect(() => {
    if (!audio) {
      return;
    }
    setError(false);
    onError && onError(null);

    const readyStateListener = () => {
      setTime(Math.ceil(audio.duration));
      setInitialized(true);
    };

    const errorListener = () => {
      onError &&
        onError({
          detail:
            "Recording could not be played. Confirm that the recording is a supported format and isn't a corrupted file.",
          trackLabel: trackLabel || url,
        });
      setError(true);
      setTime(0);
      setPlaying(false);
    };

    const trackEndedListener = function() {
      this.currentTime = 0;
      setPlaying(false);
    };

    const timeUpdatedListener = () => setTime(Math.ceil(audio.duration - audio.currentTime));

    audio.addEventListener('canplaythrough', readyStateListener);
    audio.addEventListener('error', errorListener);
    audio.addEventListener('ended', trackEndedListener);
    audio.addEventListener('timeupdate', timeUpdatedListener);

    return () => {
      audio.pause();
      audio.removeEventListener('canplaythrough', readyStateListener);
      audio.removeEventListener('ended', trackEndedListener);
      audio.removeEventListener('error', errorListener);
      audio.removeEventListener('timeupdate', timeUpdatedListener);
    };
    // eslint-disable-next-line
  }, [audio]);

  return [playing, time, toggle, hasError, setAudio, initialized];
};

export default function AudioPlayer({
  className = '',
  'data-aid': dataAid,
  enablePlayOnError,
  isPlaying = false,
  loadImmediately,
  onError,
  trackLabel,
  url,
}) {
  const [playing, time, toggle, hasError, setAudio, initialized] = useAudio({
    enablePlayOnError,
    isPlaying,
    loadImmediately,
    onError,
    trackLabel,
    url,
  });

  const timeRemaining = time ? <TimeRemaining>{formatSeconds(time)}</TimeRemaining> : null;
  const showSpinner = initialized && !time && !hasError;

  useEffect(() => {
    if (initialized) {
      setAudio(new Audio(url));
    }
  }, [url, setAudio, initialized]);

  const playingInProgress = (
    <PauseControls>
      <PauseIcon />
      <StyledSpinner />
    </PauseControls>
  );

  const errorState = hasError && !enablePlayOnError;

  const control = (
    <StyledIcon errorState={errorState}>
      {showSpinner ? <StyledSpinner /> : playing ? playingInProgress : <PlayIcon />}
    </StyledIcon>
  );

  const getPlayerContent = () => {
    if (!url) {
      return <SecondaryError>There is no URL entered</SecondaryError>;
    }

    if (hasError && !onError) {
      return (
        <PrimaryError>
          Recording could not be played. Confirm that the recording is a supported format and isn\'t a corrupted file.
        </PrimaryError>
      );
    }

    return <TrackLabel data-aid={`${dataAid}-trackName`}>{trackLabel || url}</TrackLabel>;
  };

  const content = getPlayerContent();

  return (
    <PlayerContainer className={className} data-aid={`${dataAid}-player`} errorState={errorState} onClick={toggle}>
      <PlayerControl>
        {control}
        {content}
      </PlayerControl>

      {timeRemaining}
    </PlayerContainer>
  );
}

const PlayerContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;

  &:hover {
    cursor: ${p => (p.errorState ? 'not-allowed' : 'pointer')};
  }
`;

const PlayerControl = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const StyledIcon = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  fill: ${p => (p.errorState ? p.theme.colors.gray600 : p.theme.colors.green400)};
  margin-right: 8px;

  svg {
    height: 16px;
    width: 16px;
  }
`;

export const TrackLabel = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PrimaryError = styled.div`
  color: ${p => p.theme.colors.red400};
`;

const SecondaryError = styled.div`
  color: ${p => p.theme.colors.gray600};
`;

const StyledSpinner = styled(Spinner).attrs(p => ({ color: p.theme.colors.green400, size: 16, stroke: 1 }))``;

export const TimeRemaining = styled.div``;

export const PauseControls = styled.div`
  display: flex;
  position: relative;

  *:first-child {
    position: absolute; // Places the pause icon (first-child) inside the progress indicator
  }
`;

AudioPlayer.propTypes = {
  /** integration test attribute */
  'data-aid': PropTypes.string,
  /** enables play button even if a loading error occurs. Used in combination with a custom error handler */
  enablePlayOnError: PropTypes.bool,
  /** load file and calculate duration time when component is created */
  loadImmediately: PropTypes.bool,
  /** custom error handler */
  onError: PropTypes.func,
  /** track label, if none the 'url' prop value will be used */
  trackLabel: PropTypes.string,
  /** audio download URL */
  url: PropTypes.string,
};
